import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";

const Home = lazy(() => import("../pages/home/page"));
const CoachRating = lazy(() => import("../pages/coach-rating/page"));
const StudentCard = lazy(() => import("../pages/student-card/page"));
const TournamentBracket = lazy(() => import("../pages/tournament-bracket/page"));
const TournamentList = lazy(() => import("../pages/tournament-list/page"));
const TournamentDetail = lazy(() => import("../pages/tournament-detail/page"));
const LiveScoreboard = lazy(() => import("../pages/live-scoreboard/page"));
const Login = lazy(() => import("../pages/login/page"));
const Welcome = lazy(() => import("../pages/welcome/page"));
const RoundupBoard = lazy(() => import("../pages/roundup/page"));
const RoundupOrder = lazy(() => import("../pages/roundup-order/page"));
const RoundupMyHits = lazy(() => import("../pages/roundup-my-hits/page"));
const Profile = lazy(() => import("../pages/profile/page"));
const CreateTournament = lazy(() => import("../pages/create-tournament/page"));
const TournamentHistory = lazy(() => import("../pages/tournament-history/page"));
const PickupList = lazy(() => import("../pages/pickup-list/page"));
const PickupNew = lazy(() => import("../pages/pickup-new/page"));
const PickupDetail = lazy(() => import("../pages/pickup-detail/page"));
const MatchConfirm = lazy(() => import("../pages/match-confirm/page"));
const Leaderboard = lazy(() => import("../pages/leaderboard/page"));
const UserProfilePage = lazy(() => import("../pages/user-profile/page"));
const ClubList = lazy(() => import("../pages/club-list/page"));
const ClubDetail = lazy(() => import("../pages/club-detail/page"));
const ClubNew = lazy(() => import("../pages/club-new/page"));
const ClubJoin = lazy(() => import("../pages/club-join/page"));
const ClubMatchNew = lazy(() => import("../pages/club-match-new/page"));
const ClubMatchDetail = lazy(() => import("../pages/club-match-detail/page"));
const LeaguePage = lazy(() => import("../pages/league/page"));
const AdminMatchesPage = lazy(() => import("../pages/admin-matches/page"));
const RulesPage = lazy(() => import("../pages/rules/page"));
const MyPaymentsPage = lazy(() => import("../pages/my-payments/page"));
const ApplyOrganizerPage = lazy(() => import("../pages/apply-organizer/page"));
const AdminOrganizersPage = lazy(() => import("../pages/admin-organizers/page"));
const AdminRefundsPage = lazy(() => import("../pages/admin-refunds/page"));
const AdminSponsorshipsPage = lazy(() => import("../pages/admin-sponsorships/page"));
const ChatPage = lazy(() => import("../pages/chat/page"));
const ChatsListPage = lazy(() => import("../pages/chats/page"));
const UpgradePage = lazy(() => import("../pages/upgrade/page"));
const WalletPage = lazy(() => import("../pages/wallet/page"));
const OffersPage = lazy(() => import("../pages/offers/page"));
const ChampionsPage = lazy(() => import("../pages/champions/page"));
const AdminDisputesPage = lazy(() => import("../pages/admin-disputes/page"));
const AdminAceBuzzPage = lazy(() => import("../pages/admin-acebuzz/page"));
const BusinessSignup = lazy(() => import("../pages/business-signup/page"));
const BusinessLogin = lazy(() => import("../pages/business-login/page"));
const BusinessDashboard = lazy(() => import("../pages/business-dashboard/page"));
const SponsorsMarketplace = lazy(() => import("../pages/sponsors/page"));
const SponsorDetail = lazy(() => import("../pages/sponsor-detail/page"));
const FlexLeaguePage = lazy(() => import("../pages/flex-league/page"));
const AdminFlexLeaguePage = lazy(() => import("../pages/admin-flex-league/page"));

const routes: RouteObject[] = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    // Native-App first-open landing. Minimal by design — brand + slogan
    // + Sign In. Web users don't hit this (Firebase Hosting serves the
    // marketing landing at /), but it's route-registered so we can
    // deep-link to it and the SPA router can render it.
    path: "/welcome",
    element: <Welcome />,
  },
  {
    // The Roundup board — public read (unauthenticated visitors can
    // browse; only booking / hosting requires login).
    path: "/roundup",
    element: <RoundupBoard />,
  },
  {
    // Roundup order detail — auth-only in practice (page checks user
    // is buyer or hitter), but no route guard so a deep link doesn't
    // 404 before the auth state loads. D1 stub; D3 wires actions.
    path: "/roundup/order/:id",
    element: <RoundupOrder />,
  },
  {
    path: "/roundup/my-hits",
    element: <RoundupMyHits />,
  },
  {
    path: "/",
    element: <Home />,
  },
  {
    // Dashboard alias — App's home page under a dedicated URL so
    // logged-in users can bookmark it without hitting the static
    // marketing landing served at /. Firebase Hosting serves
    // public/index.html at / (static wins over SPA rewrites), so any
    // fresh visit to acework.app/ shows marketing. Members should go
    // to /dashboard for their App home.
    path: "/dashboard",
    element: <Home />,
  },
  {
    path: "/coach-rating",
    element: <CoachRating />,
  },
  {
    path: "/student-card",
    element: <StudentCard />,
  },
  {
    path: "/tournament-list",
    element: <TournamentList />,
  },
  {
    path: "/tournament/:id",
    element: <TournamentDetail />,
  },
  {
    path: "/tournament-bracket",
    element: <TournamentBracket />,
  },
  {
    path: "/create-tournament",
    element: <CreateTournament />,
  },
  {
    path: "/tournament-history",
    element: <TournamentHistory />,
  },
  {
    path: "/live-scoreboard",
    element: <LiveScoreboard />,
  },
  {
    path: "/profile",
    element: <Profile />,
  },
  {
    path: "/u/:handle",
    element: <UserProfilePage />,
  },
  {
    path: "/leaderboard",
    element: <Leaderboard />,
  },
  {
    path: "/pickup",
    element: <PickupList />,
  },
  {
    path: "/pickup/new",
    element: <PickupNew />,
  },
  {
    path: "/pickup/:id",
    element: <PickupDetail />,
  },
  {
    path: "/match/:id/confirm",
    element: <MatchConfirm />,
  },
  {
    path: "/clubs",
    element: <ClubList />,
  },
  {
    path: "/club/new",
    element: <ClubNew />,
  },
  {
    path: "/club/:id",
    element: <ClubDetail />,
  },
  {
    path: "/c/:inviteCode",
    element: <ClubJoin />,
  },
  {
    path: "/club-match/new",
    element: <ClubMatchNew />,
  },
  {
    path: "/club-match/:id",
    element: <ClubMatchDetail />,
  },
  {
    path: "/league",
    element: <LeaguePage />,
  },
  {
    path: "/admin/matches",
    element: <AdminMatchesPage />,
  },
  {
    path: "/rules",
    element: <RulesPage />,
  },
  {
    path: "/profile/payments",
    element: <MyPaymentsPage />,
  },
  {
    path: "/apply-organizer",
    element: <ApplyOrganizerPage />,
  },
  {
    path: "/admin/organizers",
    element: <AdminOrganizersPage />,
  },
  {
    path: "/admin/refunds",
    element: <AdminRefundsPage />,
  },
  {
    path: "/admin/sponsorships",
    element: <AdminSponsorshipsPage />,
  },
  {
    path: "/chat/:chatId",
    element: <ChatPage />,
  },
  {
    path: "/chats",
    element: <ChatsListPage />,
  },
  {
    // Membership checkout gate — required signed-in landing spot before
    // Stripe checkout, so the webhook can bind the payment to a uid.
    path: "/upgrade",
    element: <UpgradePage />,
  },
  {
    // Acework Wallet — balance + full ledger.
    path: "/wallet",
    element: <WalletPage />,
  },
  {
    // Local business offers marketplace.
    path: "/offers",
    element: <OffersPage />,
  },
  {
    // Championship Wall — every completed tournament's champion.
    path: "/champions",
    element: <ChampionsPage />,
  },
  {
    // Admin dispute queue.
    path: "/admin/disputes",
    element: <AdminDisputesPage />,
  },
  {
    // Admin Ace Buzz audit — look up any user's activity ledger.
    path: "/admin/acebuzz",
    element: <AdminAceBuzzPage />,
  },
  // ----- Sponsor-side (Phase 2) -----
  {
    path: "/business/signup",
    element: <BusinessSignup />,
  },
  {
    path: "/business/login",
    element: <BusinessLogin />,
  },
  {
    path: "/business/dashboard",
    element: <BusinessDashboard />,
  },
  {
    path: "/sponsors",
    element: <SponsorsMarketplace />,
  },
  {
    path: "/sponsor/:id",
    element: <SponsorDetail />,
  },
  // ----- Flex League (Phase 1-3) -----
  {
    path: "/flex",
    element: <FlexLeaguePage />,
  },
  {
    path: "/admin/flex-league",
    element: <AdminFlexLeaguePage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
